import { useCallback, useState, useRef, useEffect } from 'react';
import { useChatStream } from './hooks/useChatStream.js';
import { useStreamInfo } from './hooks/useStreamInfo.js';
import { useChatters } from './hooks/useChatters.js';
import ChatList from './components/Chat/ChatList.js';
import ChatInput, { type ReplyTarget } from './components/Chat/ChatInput.js';
import LoginButton from './components/auth/LoginButton.js';
import { AuthProvider, useAuth } from './context/AuthContext.js';
import PrivacyPage from './pages/Privacy.js';
import AuditLogPage from './pages/AuditLog.js';
import KeyManager from './pages/KeyManager.js';
import SearchPage from './pages/SearchPage.js';
import AdminPage from './pages/AdminPage.js';
import GlobalModes from './components/Chat/GlobalModes.js';
import AutoModQueue from './components/Chat/AutoModQueue.js';
import UserInfoPanel from './components/UserInfoPanel.js';
import EventFeed from './components/Chat/EventFeed.js';

type View = 'chat' | 'privacy' | 'audit' | 'keys' | 'search' | 'admin';

function statusColor(s: string): string {
  switch (s) {
    case 'open':
      return 'bg-green-500';
    case 'connecting':
      return 'bg-yellow-500 animate-pulse';
    default:
      return 'bg-red-500';
  }
}

function statusLabel(s: string): string {
  switch (s) {
    case 'open':
      return 'verbunden';
    case 'connecting':
      return 'verbinde…';
    default:
      return 'getrennt';
  }
}

function HeaderUser({ onNavigate }: { onNavigate: (v: View) => void }) {
  const { user, isLoggedIn, isBroadcaster, loading, logout } = useAuth();
  if (loading) return <span className="text-xs text-slate-500">…</span>;
  if (!isLoggedIn || !user) return <LoginButton />;
  return (
    <div className="flex items-center gap-2">
      {user.avatar ? (
        <img
          src={user.avatar}
          alt=""
          className="w-6 h-6 rounded-full"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="w-5 h-5 rounded-full bg-purple-700 flex items-center justify-center text-[10px] font-bold text-white flex-shrink-0">
          {user.displayName[0]?.toUpperCase() ?? '?'}
        </div>
      )}
      <span className="text-xs text-slate-200" title={user.role}>
        {user.displayName}
      </span>
      <button
        type="button"
        onClick={() => onNavigate('privacy')}
        className="text-xs text-slate-400 hover:text-slate-200 px-2 py-0.5 rounded hover:bg-slate-800/70 transition-colors"
      >
        Datenschutz
      </button>
      {(user.role === 'mod' || isBroadcaster) && (
        <button
          type="button"
          onClick={() => onNavigate('search')}
          className="text-xs text-slate-400 hover:text-slate-200 px-2 py-0.5 rounded hover:bg-slate-800/70 transition-colors"
        >
          Suche
        </button>
      )}
      {isBroadcaster && (
        <button
          type="button"
          onClick={() => onNavigate('audit')}
          className="text-xs text-slate-400 hover:text-slate-200 px-2 py-0.5 rounded hover:bg-slate-800/70 transition-colors"
        >
          Audit-Log
        </button>
      )}
      {isBroadcaster && (
        <button
          type="button"
          onClick={() => onNavigate('admin')}
          className="text-xs text-slate-400 hover:text-slate-200 px-2 py-0.5 rounded hover:bg-slate-800/70 transition-colors"
        >
          Admin
        </button>
      )}
      {isBroadcaster && (
        <button
          type="button"
          onClick={() => onNavigate('keys')}
          className="text-xs text-slate-400 hover:text-slate-200 px-2 py-0.5 rounded hover:bg-slate-800/70 transition-colors"
        >
          Keys
        </button>
      )}
      <button
        type="button"
        onClick={() => void logout()}
        className="text-xs text-slate-400 hover:text-slate-200 px-2 py-0.5 rounded hover:bg-slate-800/70 transition-colors"
      >
        Logout
      </button>
    </div>
  );
}

const isEmbed = typeof window !== 'undefined'
  && new URLSearchParams(window.location.search).get('embed') === '1';

// true wenn innerhalb eines iframes (abgemiked.de), false im Meld-Studio-Dock
const isInIframe = typeof window !== 'undefined' && window.self !== window.top;

function ChatShell({ onNavigate }: { onNavigate: (v: View) => void }) {
  const { status, messages, deletedIds, quotaWarning, channelEvents, redemptionMap, chatEventsCutoff } = useChatStream();
  const { isLoggedIn, isMod } = useAuth();
  const streamInfo = useStreamInfo();
  const [openUserTwitchId, setOpenUserTwitchId] = useState<string | null>(null);
  const [autoModOpen, setAutoModOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<ReplyTarget | null>(null);
  const [showViewerList, setShowViewerList] = useState(false);
  const viewerListRef = useRef<HTMLDivElement>(null);
  const { chatters, total: chatterTotal, loading: chattersLoading, error: chattersError } =
    useChatters(showViewerList && streamInfo.live, 30_000);

  // Close viewer list on outside click
  useEffect(() => {
    if (!showViewerList) return;
    function handleClick(e: MouseEvent) {
      if (viewerListRef.current && !viewerListRef.current.contains(e.target as Node)) {
        setShowViewerList(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showViewerList]);

  const handleReply = useCallback((msg: import('./lib/twitch.js').ChatMessage) => {
    const meta = msg.source === 'twitch'
      ? (msg as { replyParentMeta?: { displayName: string; msgBody: string } | null }).replyParentMeta
      : null;
    setReplyTo({
      msgId: msg.externalId,
      displayName: msg.displayName,
      msgBody: msg.text,
    });
    void meta; // meta unused here — we use the message itself as the reply target
  }, []);

  return (
    <main className={`${isEmbed ? 'h-screen' : 'min-h-screen'} flex flex-col bg-slate-950 text-slate-100`}>
      {/* Meld Studio Dock — kompakte Viewer-Zeile oben */}
      {isEmbed && !isInIframe && (
        <div className="flex items-center justify-end gap-2 px-2 py-1 flex-shrink-0 border-b border-slate-800/60 bg-slate-900/40">
          <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${statusColor(status)}`} />
          {(streamInfo.live || streamInfo.youtubeLive) ? (
            <>
              <span className="text-[10px] font-bold text-red-400 uppercase tracking-wide">LIVE</span>
              {streamInfo.live && streamInfo.viewerCount !== null && (
                <div className="relative" ref={viewerListRef}>
                  <button
                    type="button"
                    onClick={() => setShowViewerList((v) => !v)}
                    className={`flex items-center gap-0.5 text-xs font-semibold tabular-nums px-1 py-0.5 rounded transition-colors ${
                      showViewerList
                        ? 'text-purple-300 bg-purple-900/40'
                        : 'text-slate-300 hover:text-purple-300 hover:bg-purple-900/30'
                    }`}
                    title="Viewerliste öffnen"
                  >
                    <svg className="w-3 h-3 text-purple-400 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z"/>
                    </svg>
                    {streamInfo.viewerCount.toLocaleString('de-DE')}
                  </button>
                  {showViewerList && (
                    <div className="absolute right-0 top-full mt-1 w-56 max-h-80 flex flex-col bg-slate-900 border border-slate-700 rounded shadow-xl z-50">
                      <div className="flex items-center justify-between px-2 py-1.5 border-b border-slate-700 flex-shrink-0">
                        <span className="text-[10px] font-bold text-purple-400 uppercase tracking-wide">
                          Viewer im Chat
                        </span>
                        <span className="text-[10px] text-slate-400 tabular-nums">
                          {chattersLoading ? '…' : chatterTotal.toLocaleString('de-DE')}
                        </span>
                      </div>
                      <div className="overflow-y-auto flex-1">
                        {chattersError ? (
                          <p className="text-[10px] text-red-400 px-2 py-1">{chattersError}</p>
                        ) : chatters.length === 0 && !chattersLoading ? (
                          <p className="text-[10px] text-slate-500 px-2 py-1">Keine Daten</p>
                        ) : (() => {
                          // Group by role and render with section headers
                          const groups: Array<{ role: import('./hooks/useChatters.js').ChatterRole; label: string; color: string }> = [
                            { role: 'broadcaster', label: 'Broadcaster', color: 'text-yellow-400' },
                            { role: 'mod',         label: 'Moderatoren', color: 'text-green-400' },
                            { role: 'vip',         label: 'VIPs',        color: 'text-pink-400'  },
                            { role: 'viewer',      label: 'Viewer',      color: 'text-slate-400' },
                          ];
                          const nameColor: Record<import('./hooks/useChatters.js').ChatterRole, string> = {
                            broadcaster: 'text-yellow-300',
                            mod:         'text-green-300',
                            vip:         'text-pink-300',
                            viewer:      'text-slate-300',
                          };
                          return groups.map(({ role, label, color }) => {
                            const members = chatters.filter((c) => c.role === role);
                            if (members.length === 0) return null;
                            return (
                              <div key={role}>
                                <div className={`px-2 pt-1.5 pb-0.5 text-[9px] font-bold uppercase tracking-wider ${color}`}>
                                  {label} ({members.length})
                                </div>
                                {members.map((c) => (
                                  <div key={c.user_id} className={`px-3 py-0.5 text-[11px] hover:bg-slate-800 truncate ${nameColor[role]}`}>
                                    {c.user_name}
                                  </div>
                                ))}
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {streamInfo.youtubeLive && streamInfo.youtubeViewerCount !== null && (
                <span className="flex items-center gap-0.5 text-xs text-slate-300 font-semibold tabular-nums">
                  <svg className="w-3 h-3 text-red-500 flex-shrink-0" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z"/>
                  </svg>
                  {streamInfo.youtubeViewerCount.toLocaleString('de-DE')}
                </span>
              )}
            </>
          ) : (
            <span className="text-[10px] text-slate-600">offline</span>
          )}
        </div>
      )}
      {!isEmbed && (
      <header className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-800 flex-shrink-0">
        <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${statusColor(status)}`} />
        <h1 className="text-base font-semibold">Chat-Tool</h1>
        <span className="text-xs text-slate-400">{statusLabel(status)}</span>
        <span className="text-xs text-slate-500">{messages.length} msgs</span>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {isMod && (
            <button
              type="button"
              onClick={() => setAutoModOpen((v) => !v)}
              className={`text-xs px-2 py-0.5 rounded transition-colors ${
                autoModOpen
                  ? 'text-purple-300 bg-purple-900/40'
                  : 'text-purple-400 hover:text-purple-300 hover:bg-purple-900/30'
              }`}
            >
              AutoMod
            </button>
          )}
          <HeaderUser onNavigate={onNavigate} />
        </div>
      </header>
      )}
      {isMod && (!isEmbed || !isInIframe) && <GlobalModes />}
      {isMod && <EventFeed events={channelEvents} />}
      <div className="flex-1 min-h-0 flex">
        <div className="flex-1 min-w-0 flex flex-col">
          <ChatList
            messages={messages}
            deletedIds={deletedIds}
            quotaWarning={quotaWarning}
            onOpenUserInfo={(twitchUserId) => setOpenUserTwitchId(twitchUserId)}
            redemptionMap={redemptionMap}
            onReply={isLoggedIn ? handleReply : undefined}
            channelEvents={channelEvents}
            chatEventsCutoff={chatEventsCutoff}
          />
        </div>
        {isMod && autoModOpen && <AutoModQueue />}
      </div>
      {isLoggedIn ? (
        <ChatInput replyTo={replyTo} onClearReply={() => setReplyTo(null)} />
      ) : (
        <div className="border-t border-slate-800 bg-slate-900/60 px-4 py-3 flex items-center gap-3 flex-shrink-0">
          <span className="text-sm text-slate-400">Logge dich ein, um zu schreiben.</span>
          {isEmbed ? (
            isInIframe ? (
              // Innerhalb abgemiked.de-iframe: neues Tab öffnen
              <a
                href="https://chat.abgemiked.de/api/auth/twitch"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-purple-400 hover:text-purple-300 underline"
              >
                Login ↗
              </a>
            ) : (
              // Meld-Studio-Dock: im Webview selbst navigieren
              <a
                href="/api/auth/twitch"
                className="text-sm text-purple-400 hover:text-purple-300 underline"
              >
                Login
              </a>
            )
          ) : (
            <LoginButton />
          )}
        </div>
      )}
      {openUserTwitchId && (
        <UserInfoPanel
          userId={openUserTwitchId}
          lookupBy="twitch"
          onClose={() => setOpenUserTwitchId(null)}
        />
      )}
    </main>
  );
}

function AppShell() {
  const [view, setView] = useState<View>('chat');
  if (view === 'privacy') return <PrivacyPage onClose={() => setView('chat')} />;
  if (view === 'audit') return <AuditLogPage onClose={() => setView('chat')} />;
  if (view === 'search') return <SearchPage onClose={() => setView('chat')} />;
  if (view === 'admin') return <AdminPage onClose={() => setView('chat')} />;
  if (view === 'keys') return <KeyManager onClose={() => setView('chat')} />;
  return <ChatShell onNavigate={setView} />;
}

export default function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}
